import { memo, useMemo } from 'react'
import { StyleSheet, View } from 'react-native'
import { Icon } from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useInViewport } from '@/hooks/monitor/useInViewport'
import type { LocationCircle } from '@/hooks/useActiveLocations'
import { hexToRgba } from '@/lib/color-utils'
import { getMarkerVisual } from '@/lib/monitor/markerVisual'
import { getMovementInfo } from '@/lib/movement-utils'
import type { UserMarkerVariant } from '@/types/mapData'
import type { PathPoint } from '@/types/movement'
import { MiniMap, type MiniMapCircle, type MiniMapMarker } from '@/ui/map/MiniMap'

interface Props {
    location: PathPoint
    userColor: string
    variant: UserMarkerVariant
    siteCircle?: LocationCircle | null
    width?: number
    height?: number
}

const MARKER_SIZE = 28

export const UserCardMiniMap = memo(function UserCardMiniMap({
    location,
    userColor,
    variant,
    siteCircle,
    width = 160,
    height = 100,
}: Props) {
    const { ref, visible } = useInViewport<HTMLDivElement>('200px')
    const semantic = useSemanticColors()

    const coordinates = useMemo(
        () => [{ latitude: location.latitude, longitude: location.longitude }],
        [location.latitude, location.longitude]
    )

    const circles = useMemo<MiniMapCircle[]>(() => {
        if (!siteCircle) return []
        return [
            {
                latitude: siteCircle.latitude,
                longitude: siteCircle.longitude,
                radius: siteCircle.radius,
                strokeColor: userColor,
                fillColor: hexToRgba(userColor, 0.18),
            },
        ]
    }, [siteCircle, userColor])

    const markers = useMemo<MiniMapMarker[]>(() => {
        const { icon, rotation } = getMovementInfo(
            location.is_moving,
            location.movement_type,
            location.heading,
            location.speed
        )
        const visual = getMarkerVisual(variant, icon, userColor, semantic.warning)
        // Only apply rotation for the moving variant — a parked worker's
        // heading is stale and the big map doesn't rotate their marker either.
        const markerRotation = variant === 'moving' ? rotation : 0
        const shouldFlip = icon !== 'navigation'

        return [
            {
                coordinate: coordinates[0],
                rotation: markerRotation,
                shouldFlip,
                content: (
                    <View
                        style={[
                            styles.marker,
                            {
                                width: MARKER_SIZE,
                                height: MARKER_SIZE,
                                borderRadius: MARKER_SIZE / 2,
                                backgroundColor: visual.bg,
                                borderColor: visual.border,
                                borderStyle: visual.dashed ? 'dashed' : 'solid',
                                borderWidth: visual.filled ? 0 : 2,
                            },
                        ]}
                    >
                        <Icon
                            source={visual.glyph}
                            size={Math.round(MARKER_SIZE * 0.62)}
                            color={visual.fg}
                        />
                    </View>
                ),
            },
        ]
    }, [
        coordinates,
        location.is_moving,
        location.movement_type,
        location.heading,
        location.speed,
        userColor,
        variant,
        semantic.warning,
    ])

    return (
        <View
            ref={ref as unknown as React.Ref<View>}
            style={[styles.wrap, { width, height, backgroundColor: hexToRgba(userColor, 0.08) }]}
        >
            {visible ? (
                <MiniMap
                    coordinates={coordinates}
                    markers={markers}
                    circles={circles}
                    minDelta={0.003}
                    style={styles.map}
                />
            ) : null}
        </View>
    )
})

const styles = StyleSheet.create({
    wrap: {
        borderRadius: 8,
        overflow: 'hidden',
    },
    map: {
        flex: 1,
    },
    marker: {
        justifyContent: 'center',
        alignItems: 'center',
    },
})
